import { ReactNode } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Dialog from '@mui/material/Dialog';

import { Breakpoint } from '@mui/material';
import useBreakpoint from 'utilities/use-breakpoint';
import { DialogFooter, DialogFooterAction } from './dialog-footer';
import { DialogHeader } from './dialog-header';
import {
  DialogContainer,
  DialogContentContainer,
  DialogContentFormContainer,
  DialogMainContent,
} from './dialogStyledComponents';
import { useTracking } from '../tracking';

interface CompositeDialogProps {
  open: boolean;
  handleClose?: () => void;
  icon?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  maxWidth?: false | Breakpoint;
  preventMobileFullscreen?: boolean;
  handleFormSubmit?: () => unknown | void | Promise<void>;
  actions?: DialogFooterAction[];
}

const ConditionalDialogContentContainer = (props: {
  isForm: boolean;
  handleFormSubmit: (data: unknown) => unknown | void;
  children: ReactNode;
}) =>
  props.isForm ? (
    <DialogContentFormContainer onSubmit={data => props.handleFormSubmit(data)}>
      {props.children}
    </DialogContentFormContainer>
  ) : (
    <DialogContentContainer>{props.children}</DialogContentContainer>
  );

const CompositeDialogContent = (props: CompositeDialogProps) => {
  const { mobile } = useBreakpoint();
  const { generateTrackingProps } = useTracking();

  // Enhance actions with tracking props
  const enhancedActions = props.actions?.map(action => {
    const trackingProps = generateTrackingProps(
      action.text.toLowerCase().replace(/\s+/g, '-')
    );

    return {
      ...action,
      trackingProps,
    };
  });

  return (
    <DialogContainer>
      <Dialog
        fullScreen={!props.preventMobileFullscreen && mobile}
        maxWidth={props.maxWidth ?? 'xl'}
        onClose={props.handleClose}
        open={props.open}
        PaperProps={{
          sx: {
            height: 'auto',
            maxHeight: mobile ? '100%' : 'auto',
          },
        }}
      >
        <ConditionalDialogContentContainer
          isForm={!!props.handleFormSubmit}
          handleFormSubmit={props.handleFormSubmit}
        >
          <DialogHeader
            prefixIcon={props.icon}
            postfixAction={{
              title: 'close',
              icon: <CloseIcon />,
              callback: props.handleClose,
            }}
          >
            {props.title}
          </DialogHeader>
          <DialogMainContent>{props.children}</DialogMainContent>
          {!!enhancedActions?.length && (
            <DialogFooter actions={enhancedActions} />
          )}
        </ConditionalDialogContentContainer>
      </Dialog>
    </DialogContainer>
  );
};

const CompositeDialog = (props: CompositeDialogProps) => {
  // Since we now have a root tracking provider, we can always render the content
  return <CompositeDialogContent {...props} />;
};

export default CompositeDialog;
